import { useEffect, useRef } from 'react'

import { CustomEvent } from '../../../../../../../enums/customEvent'
import { removeReviewEntryDraft, saveReviewEntryDraft } from '../../../../../utils/reviewEntryDraftStorage'

export const REVIEW_ENTRY_DRAFT_AUTOSAVE_DEBOUNCE_MS = 250

interface UseReviewEntryDraftAutosaveProps {
  entryId?: string
  isFormReady: boolean
  isReadOnly: boolean
  isSubmitting?: boolean
  organizationId?: string
  sourceFingerprint?: string
  watch: (callback: (values: unknown) => void) => { unsubscribe: () => void }
}

export const useReviewEntryDraftAutosave = ({
  entryId,
  isFormReady,
  isReadOnly,
  isSubmitting = false,
  organizationId,
  sourceFingerprint,
  watch,
}: UseReviewEntryDraftAutosaveProps) => {
  const shouldPersistDraftRef = useRef(false)
  shouldPersistDraftRef.current = Boolean(entryId && organizationId && isFormReady && !isReadOnly)

  useEffect(() => {
    if (!entryId || !organizationId || !isReadOnly) {
      return
    }

    removeReviewEntryDraft({ entryId, organizationId })
  }, [entryId, isReadOnly, organizationId])

  useEffect(() => {
    if (!entryId || !organizationId || !isFormReady || isReadOnly || isSubmitting) {
      return
    }

    const draftKey = { entryId, organizationId, sourceFingerprint }
    let debounceTimeoutId: ReturnType<typeof globalThis.setTimeout> | undefined
    let pendingValues: unknown

    const { unsubscribe } = watch((formValues) => {
      pendingValues = formValues
      globalThis.clearTimeout(debounceTimeoutId)
      debounceTimeoutId = globalThis.setTimeout(() => {
        saveReviewEntryDraft(draftKey, formValues)
        pendingValues = undefined
      }, REVIEW_ENTRY_DRAFT_AUTOSAVE_DEBOUNCE_MS)
    })

    const flushPendingDraft = () => {
      if (pendingValues === undefined) {
        return
      }

      globalThis.clearTimeout(debounceTimeoutId)
      saveReviewEntryDraft(draftKey, pendingValues)
      pendingValues = undefined
    }

    globalThis.addEventListener(CustomEvent.ReviewEntryDraftFlushRequested, flushPendingDraft)

    return () => {
      unsubscribe()
      globalThis.removeEventListener(CustomEvent.ReviewEntryDraftFlushRequested, flushPendingDraft)
      globalThis.clearTimeout(debounceTimeoutId)

      if (pendingValues !== undefined && shouldPersistDraftRef.current) {
        saveReviewEntryDraft(draftKey, pendingValues)
      }
    }
  }, [entryId, isFormReady, isReadOnly, isSubmitting, organizationId, sourceFingerprint, watch])
}
